import { useState } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router";

import type { AutomatedSurveyEditRequest, AutomatedSurveyRequest, SimpleProjectDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import {
  DynamicRepeater,
  type DynamicRepeaterDragEndEvent,
  type DynamicRepeaterDragStartEvent,
} from "@/components/DynamicRepeater/DynamicRepeater";
import { DynamicRepeaterItem } from "@/components/DynamicRepeater/DynamicRepeaterItem";
import { DynamicRepeaterItemOverlay } from "@/components/DynamicRepeater/DynamicRepeaterItemOverlay";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { createRequiredStringRule } from "@/helpers/rules";
import { parseAsNumber, sortAlphabetically } from "@/helpers/util";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import type {
  AutomatedSurveyQuestionCategory,
  AutomatedSurveyQuestionCategoryId,
  AutomatedSurveyType,
} from "../../constants";
import { automatedSurveyQuestionCategories, automatedSurveyTypes } from "../../constants";
import { FormQuestion } from "./components/FormQuestion";

interface Question {
  qId?: string;
  category: AutomatedSurveyQuestionCategory;
  explanation: string;
  ratingQuestion: string;
  openQuestion: string;
}
export interface FormValues {
  name: string;
  description: string;
  thanksMessage: string;
  interval: string;
  minSampleSize: string;
  type: AutomatedSurveyType;
  projects: SimpleProjectDto[];
  active: boolean;
  questions: Question[];
}
export interface AutomatedSurveyFormProps {
  defaultFormValues: FormValues;
  mode: "create" | "edit";
  surveyId?: string;
  onSubmit: (payload: AutomatedSurveyRequest | AutomatedSurveyEditRequest) => Promise<unknown>;
  isSubmitting: boolean;
}
const MAX_LENGTH_NAME = 255;
const MAX_LENGTH_DESCRIPTION = 5000;
const MAX_LENGTH_THANK_YOU_MESSAGE = 5000;
const MIN_INTERVAL = 1;
const MIN_SAMPLE_SIZE = 1;
export function AutomatedSurveyForm({
  defaultFormValues,
  mode,
  surveyId,
  onSubmit,
  isSubmitting,
}: AutomatedSurveyFormProps): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSaveAndActivateModalOpen, saveAndActivateModalHandler] = useBool(false);
  const [isGoBackModalOpen, goBackModalHandler] = useBool(false);
  const [isMissingCatModalOpen, missingCatModalHandler] = useBool(false);
  const [missingCategories, setMissingCategories] = useState<string[]>([]);
  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
  });
  function getQuestionCategoryLabel(x: AutomatedSurveyQuestionCategoryId) {
    switch (x) {
      case "building":
        return t("page.automated-surveys.question-type.building");
      case "community":
        return t("page.automated-surveys.question-type.community");
      case "service":
        return t("page.automated-surveys.question-type.service");
      case "feelAtHome":
        return t("page.automated-surveys.question-type.feeling-at-home");
      case "other":
        return t("page.automated-surveys.types.other");
    }
  }
  function checkMandatoryQuestionCategories() {
    const questions = form.getValues("questions");
    const missingCategories = [];
    const mandatoryCategories = automatedSurveyQuestionCategories.filter((category) => category.mandatory);
    for (let i = 0; i < mandatoryCategories.length; i++) {
      let count = 0;
      for (let j = 0; j < questions.length; j++) {
        if (questions[j].category.id === mandatoryCategories[i].id) {
          count++;
        }
      }
      if (count === 0) {
        missingCategories.push(getQuestionCategoryLabel(mandatoryCategories[i].id));
      }
    }

    return {
      isValid: !missingCategories.length,
      missingCategories,
    };
  }
  async function handleCreateAutomatedSurvey() {
    saveAndActivateModalHandler.setFalse();
    const values = form.getValues();

    const payload = {
      title: values.name,
      description: values.description,
      thankYouMessage: values.thanksMessage,
      titleTranslations: [],
      descriptionTranslations: [],
      thankYouMessageTranslations: [],
      weeklyInterval: parseAsNumber(values.interval) || 0,
      usersSampleSize: parseAsNumber(values.minSampleSize) || 0,
      category: values.type,
      questionGroups: values.questions.map((value, idx) => ({
        ratingQuestionTemplate: value.ratingQuestion,
        openQuestionTemplate: value.openQuestion,
        explanation: value.explanation,
        ratingQuestionTemplateTranslations: [],
        openQuestionTemplateTranslations: [],
        explanationTranslations: [],
        type: value.category.id,
        order: idx,
      })),
      enabledProjectsIds: values.projects.map((project) => project.id),
      isLocked: values.active,
    } satisfies AutomatedSurveyRequest;

    await onSubmit(payload);
  }
  async function handleEditAutomatedSurvey() {
    saveAndActivateModalHandler.setFalse();
    const values = form.getValues();
    const payload = {
      id: surveyId!,
      title: values.name,
      description: values.description,
      thankYouMessage: values.thanksMessage,
      titleTranslations: [],
      descriptionTranslations: [],
      thankYouMessageTranslations: [],
      weeklyInterval: parseAsNumber(values.interval) || 0,
      usersSampleSize: parseAsNumber(values.minSampleSize) || 0,
      category: values.type,
      questionGroups: values.questions.map((value, idx) => ({
        id: value.qId,
        ratingQuestionTemplate: value.ratingQuestion,
        explanation: value.explanation,
        openQuestionTemplate: value.openQuestion,
        ratingQuestionTemplateTranslations: [],
        explanationTranslations: [],
        openQuestionTemplateTranslations: [],
        type: value.category.id,
        order: idx,
      })),
      enabledProjectsIds: values.projects.map((project) => project.id),
      isLocked: values.active,
    } satisfies AutomatedSurveyEditRequest;
    await onSubmit(payload);
  }
  async function handleSave() {
    const mandatoryCategoriesCheck = checkMandatoryQuestionCategories();
    if (!mandatoryCategoriesCheck.isValid) {
      setMissingCategories(mandatoryCategoriesCheck.missingCategories);
      missingCatModalHandler.setTrue();
    } else if (!defaultFormValues.active && form.getValues("active")) {
      saveAndActivateModalHandler.setTrue();
    } else {
      if (mode === "edit") {
        await handleEditAutomatedSurvey();
      } else {
        await handleCreateAutomatedSurvey();
      }
    }
  }

  const title = mode === "edit" ? t("page.automated-surveys.edit.title") : t("page.automated-surveys.new.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.automated-surveys.title"),
              to: routes.automatedSurveys.list(),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={form} onSubmit={handleSave}>
        <Form.Container>
          <QuestionFormContent defaultFormValues={defaultFormValues} isSubmitting={isSubmitting} />
        </Form.Container>
      </Form.Root>
      <ConfirmModal
        tone="primary"
        title={t("page.automated-surveys.new.save-active-modal.title")}
        description={t("page.automated-surveys.new.save-active-modal.description")}
        isLoading={false}
        onReject={saveAndActivateModalHandler.setFalse}
        onOpenChange={saveAndActivateModalHandler.set}
        rejectBtnProps={{
          "data-testid": "automated-survey-save-active-modal-cancel",
        }}
        onResolve={() => {
          if (mode === "edit") {
            void handleEditAutomatedSurvey();
          } else {
            void handleCreateAutomatedSurvey();
          }
          saveAndActivateModalHandler.setFalse();
        }}
        resolveBtnProps={{
          text: t("common.action.confirm"),
          "data-testid": "automated-survey-save-active-modal-confirm",
        }}
        isOpened={isSaveAndActivateModalOpen}
        shouldCloseOnEsc
        data-testid="automated-survey-save-active-modal"
      />
      <ConfirmModal
        title={t("page.automated-surveys.new.go-back-modal.title")}
        description={t("page.automated-surveys.new.go-back-modal.description")}
        isLoading={false}
        tone="primary"
        isOpened={isGoBackModalOpen}
        shouldCloseOnEsc
        onReject={goBackModalHandler.setFalse}
        onOpenChange={goBackModalHandler.set}
        rejectBtnProps={{
          "data-testid": "automated-survey-go-back-modal-cancel",
        }}
        onResolve={() => void navigate(routes.automatedSurveys.list())}
        resolveBtnProps={{
          text: t("common.action.confirm"),
          "data-testid": "automated-survey-go-back-modal-confirm",
        }}
        data-testid="automated-survey-go-back-modal"
      />
      <ConfirmModal
        title={t("page.automated-surveys.new.missing-questions-modal.title")}
        description={t("page.automated-surveys.new.missing-questions-modal.description", {
          categories: missingCategories.join(", "),
        })}
        tone="primary"
        isLoading={false}
        onReject={missingCatModalHandler.setFalse}
        onOpenChange={missingCatModalHandler.set}
        rejectBtnProps={{
          "data-testid": "automated-survey-missing-questions-modal-cancel",
        }}
        onResolve={missingCatModalHandler.setFalse}
        resolveBtnProps={{
          text: t("common.action.confirm"),
          "data-testid": "automated-survey-missing-questions-modal-confirm",
        }}
        isOpened={isMissingCatModalOpen}
        shouldCloseOnEsc
        data-testid="automated-survey-missing-questions-modal"
      />
    </DocumentPage>
  );
}
function QuestionFormContent({
  defaultFormValues,
  isSubmitting,
}: {
  defaultFormValues: FormValues;
  isSubmitting: boolean;
}) {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const { data: projects = [] } = useLinkedProjects();
  const [dragItemId, setDragItemId] = useState<number | null>(null);
  const { setValue } = useFormContext<FormValues>();
  const { fields, update, append, remove, move } = useFieldArray<FormValues, "questions">({
    name: "questions",
  });
  function addAllProjects() {
    setValue("projects", projects);
  }
  function clearProjectSelection() {
    setValue("projects", []);
  }
  function handleDragStart(event: DynamicRepeaterDragStartEvent) {
    setDragItemId(event.active.data.current?.sortable.index);
  }
  function handleDragEnd(event: DynamicRepeaterDragEndEvent) {
    setDragItemId(null);
    const { active, over } = event;
    if (over && active.id !== over.id) {
      move(active.data.current?.sortable.index, over?.data.current?.sortable.index);
    }
  }

  return (
    <>
      <Form.Body>
        <FormField label={t("page.automated-surveys.new.form.name.label")} required>
          <FormInput<FormValues>
            name="name"
            placeholder={t("page.automated-surveys.new.form.name.placeholder")}
            rules={{
              maxLength: {
                message: t("components.form.error.max-length", {
                  length: MAX_LENGTH_NAME,
                }),
                value: MAX_LENGTH_NAME,
              },
              validate: {
                required: createRequiredStringRule(t, "page.automated-surveys.new.form.name.label"),
              },
            }}
          />
        </FormField>
        <Form.SplitContent>
          <FormField label={t("page.automated-surveys.new.form.description.label")} required>
            <FormTextArea<FormValues>
              name="description"
              placeholder={t("page.automated-surveys.new.form.description.placeholder")}
              rules={{
                maxLength: {
                  message: t("components.form.error.max-length", {
                    length: MAX_LENGTH_DESCRIPTION,
                  }),
                  value: MAX_LENGTH_DESCRIPTION,
                },
                validate: {
                  required: createRequiredStringRule(t, "page.automated-surveys.new.form.description.label"),
                },
              }}
            />
          </FormField>
          <FormField label={t("page.automated-surveys.new.form.thanks-message.label")} required>
            <FormTextArea<FormValues>
              name="thanksMessage"
              placeholder={t("page.automated-surveys.new.form.thanks-message.placeholder")}
              rules={{
                maxLength: {
                  message: t("components.form.error.max-length", {
                    length: MAX_LENGTH_THANK_YOU_MESSAGE,
                  }),
                  value: MAX_LENGTH_THANK_YOU_MESSAGE,
                },
                validate: {
                  required: createRequiredStringRule(t, "page.automated-surveys.new.form.thanks-message.label"),
                },
              }}
            />
          </FormField>
        </Form.SplitContent>
        <Form.SplitContent>
          <FormField label={t("page.automated-surveys.new.form.interval.label")} required>
            <FormInput<FormValues>
              name="interval"
              placeholder={t("page.automated-surveys.new.form.interval.placeholder")}
              inputMode="numeric"
              type="number"
              rules={{
                required: t("components.form.error.required", {
                  inputName: t("page.automated-surveys.new.form.interval.label"),
                }),
                min: {
                  message: t("page.automated-surveys.new.form.interval.rules.min", {
                    count: MIN_INTERVAL,
                  }),
                  value: MIN_INTERVAL,
                },
              }}
            />
          </FormField>
          <FormField label={t("page.automated-surveys.new.form.min-sample.label")} required>
            <FormInput<FormValues>
              name="minSampleSize"
              placeholder={t("page.automated-surveys.new.form.min-sample.placeholder")}
              inputMode="numeric"
              type="number"
              rules={{
                required: t("components.form.error.required", {
                  inputName: t("page.automated-surveys.new.form.min-sample.label"),
                }),
                min: {
                  message: t("page.automated-surveys.new.form.min-sample.rules.min", {
                    count: MIN_SAMPLE_SIZE,
                  }),
                  value: MIN_SAMPLE_SIZE,
                },
              }}
            />
          </FormField>
          <FormField label={t("page.automated-surveys.new.form.type.label")} required>
            <FormSelect<FormValues, AutomatedSurveyType>
              name="type"
              placeholder={t("page.automated-surveys.new.form.type.placeholder")}
              items={automatedSurveyTypes}
              keySelector={(x) => x}
              renderOption={(x) => {
                switch (x) {
                  case "other":
                    return t("page.automated-surveys.types.other");
                  case "feelAtHome":
                    return t("page.automated-surveys.types.feeling-at-home");
                }
              }}
              rules={{
                required: t("components.form.error.required", {
                  inputName: t("page.automated-surveys.new.form.type.label"),
                }),
              }}
              disabled={defaultFormValues.active}
            />
          </FormField>
        </Form.SplitContent>
        <section className="flex flex-col items-start gap-space-sm @sm:flex-row @sm:items-end">
          <FormField label={t("page.automated-surveys.new.form.projects.label")} required>
            <FormMultiSelect<FormValues, "projects">
              name="projects"
              placeholder={t("page.automated-surveys.new.form.projects.placeholder")}
              items={projects || []}
              searchableFieldSelector={(project) => [project.name, project.owner, project.maintenance]}
              renderOption={(project) => project.name}
              keySelector={(project) => project.id}
              sortSelectedItems={sortAlphabetically}
              rules={{
                required: t("components.form.error.required", {
                  inputName: t("page.automated-surveys.new.form.projects.label"),
                }),
              }}
            />
          </FormField>
          <Inline gap="sm" wrap={false}>
            <Button variant="soft" onClick={addAllProjects}>
              {t("component.create-message-multiple-projects.form.to.add-all")}
            </Button>
            <Button variant="subtle" tone="error" onClick={clearProjectSelection}>
              {t("component.create-message-multiple-projects.form.to.clear-all")}
            </Button>
          </Inline>
        </section>
        <Card as="section" borderTone="secondary">
          <DynamicRepeater
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            itemIds={fields.map((field) => field.id)}
          >
            {fields.map((question, idx) => (
              <DynamicRepeaterItem
                key={question.id}
                id={question.id}
                title={t("page.automated-surveys.new.question.label", {
                  count: idx + 1,
                })}
                disabled={defaultFormValues.active}
              >
                <FormQuestion
                  idx={idx}
                  question={question}
                  categories={
                    sessionUser.isSuperAdmin
                      ? automatedSurveyQuestionCategories
                      : automatedSurveyQuestionCategories.filter((cat) => cat.id !== "feelAtHome")
                  }
                  showDelete={
                    !defaultFormValues.active &&
                    (question.category.mandatory
                      ? fields.reduce((counter, field) => {
                          if (question.category.id === field.category.id) {
                            counter += 1;
                          }

                          return counter;
                        }, 0) > 1
                      : true)
                  }
                  updateField={update}
                  removeField={remove}
                  disabled={defaultFormValues.active}
                />
              </DynamicRepeaterItem>
            ))}
            <DynamicRepeaterItemOverlay
              show={dragItemId !== null}
              title={t("page.automated-surveys.new.question.label", {
                count: dragItemId! + 1,
              })}
            >
              <FormQuestion
                idx={dragItemId!}
                question={fields[dragItemId!]}
                categories={automatedSurveyQuestionCategories}
                showDelete={false}
                updateField={() => null}
                removeField={() => null}
                disabled={defaultFormValues.active}
              />
            </DynamicRepeaterItemOverlay>
          </DynamicRepeater>
        </Card>
        {!defaultFormValues.active && (
          <Button
            variant="soft"
            onClick={() =>
              append({
                category: automatedSurveyQuestionCategories[0],
                explanation: "",
                ratingQuestion: "",
                openQuestion: "",
              })
            }
          >
            {t("page.automated-surveys.new.question.add")}
          </Button>
        )}
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button type="submit" isLoading={isSubmitting}>
            {t("page.automated-surveys.new.form.submit")}
          </Button>
        )}
        renderTertiaryAction={() => (
          <FormCheckbox<FormValues>
            name="active"
            label={t("page.automated-surveys.new.form.active.label")}
            disabled={defaultFormValues.active}
          />
        )}
      />
    </>
  );
}
